import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '@env/environment';

import { Document } from '@model/Document';
import { PagedData } from '@model/datatable/paged-data';
import { Attachment } from '@model/Attachment';
import { Paginate } from '@model/Paginate';
import { DocHistory } from '@model/DocHistory';

export type AttachmentType = 'appendices_filename' | 'attachments_filename';

@Injectable({providedIn: 'root'})
export class DocumentService {

  static propertyPushed = new EventEmitter<string>();

  constructor(private http: HttpClient) {
  }

  static pushProperty(property: string) {
    DocumentService.propertyPushed.emit(property);
  }

  paginateReferencesStudies(
    projectId: number,
    per_page: number = 5,
    page: number = 1,
    term: string = ''
  ): Observable<PagedData<any>> {
    return this.http.get<PagedData<any>>(`${environment.base_url}/documents/${projectId}/references?page=${page}&per_page=${per_page}&q=${term}`);
  }

  getBySearchReferencesStudies(projectId: number, term: string = ''): Observable<any> {
    return this.http.get(`${environment.base_url}/documents/${projectId}/search_references_studies?q=${term}`);
  }

  get(projectId: number, property: string = ''): Observable<any> {
    return this.http.get<any>(`${environment.base_url}/documents/${projectId}${property.length ? '/?only='+property : '/'}`);
  }

  update(documentId: number, form: any): Observable<any> {
    return this.http.put(`${environment.base_url}/documents/${documentId}`, form);
  }

  uploadImage(projectId: number, file: File) {
    const payload = new FormData();

    payload.append('file', file);
    payload.append('project_id', JSON.stringify(projectId));

    return this.http.post(`${environment.base_url}/projects/images/upload`, payload, {
      reportProgress: true,
      observe: 'events'
    });
  }

  paginateImages(projectId: number, per_page: number = 10, page: number = 1): Observable<PagedData<any>> {
    return this.http.get<PagedData<any>>(`${environment.base_url}/projects/${projectId}/images?page=${page}&per_page=${per_page}`);
  }

  deleteImage(id: number): Observable<any> {
    return this.http.delete(`${environment.base_url}/images/${id}`);
  }

  pdf(project_id: number, content: string): Observable<any> {
    const url = `${environment.base_url}/documents/pdf_gen`;
    // return this.http.get<Blob>(url, {
    //   observe: 'response',
    //   responseType: 'blob' as 'json'
    // });
    return this.http.post<any>(url, { project_id, content }, {
      observe: 'events',
      responseType: 'blob' as 'json',
      reportProgress: true
    });
  }

  countPages(projectId: number): Observable<any> {
    return this.http.get(`${environment.base_url}/documents/pdf/count_pages/${projectId}`);
  }

  sync(projectId: number, content: string): Observable<any> {
    return this.http.post<any>(`${environment.base_url}/documents/sync`, {
      project_id: projectId,
      content
    });
  }

  checklistImage(projectId: number, base64: string): Observable<any> {
    return this.http.post<any>(`${environment.base_url}/documents/checklist_image`, {
      projectId,
      checklist_html: base64
    });
  }

  uploadCatalogCardPdf(documentId: number, file: File) {
    const payload = new FormData();
    payload.append('file', file);
    payload.append('document_id', JSON.stringify(documentId));

    return this.http.post(`${environment.base_url}/documents/upload_catalog_card`, payload, {
      reportProgress: true,
      observe: 'events'
    });
  }

  uploadApprovalSheetPdf(documentId: number, file: File) {
    const payload = new FormData();
    payload.append('file', file);
    payload.append('document_id', JSON.stringify(documentId));

    return this.http.post(`${environment.base_url}/documents/upload_approval_sheet`, payload, {
      reportProgress: true,
      observe: 'events'
    });
  }

  uploadAppendiceOrAttachment(documentId: number, file: File, attachmentType: AttachmentType = 'appendices_filename') {
    const payload = new FormData();
    payload.append('file', file);
    payload.append('document_id', JSON.stringify(documentId));
    payload.append('attachment_type', attachmentType);

    return this.http.post(`${environment.base_url}/documents/upload_appendice_or_attachment`, payload, {
      reportProgress: true,
      observe: 'events'
    });
  }

  /* Delete Appendices or Attachments */
  deleteAttachment(attachment: { document_id: number; attachment_type: AttachmentType }): Observable<any> {
    return this.http.post(`${environment.base_url}/documents/delete_appendice_or_attachment`, attachment);
  }

  checkInUse (projectId: number, userId: number): Observable<any> {
    return this.http.get(`${environment.base_url}/documents/${projectId}/${userId}/check_in_use`);
  }

  enable (projectId: number, userId: number): Observable<any> {
    return this.http.get(`${environment.base_url}/documents/${projectId}/${userId}/enable`);
  }

  saveHistory(projectId: number): Observable<any> {
    return this.http.post(`${environment.base_url}/documents/save_history`, {
      project_id: projectId
    });
  }

  // getAllHistory(projectId: number): Observable<any> {
  //   return this.http.get(`${environment.base_url}/documents/${projectId}/get_all_history`);
  // }

  getAllHistory(projectId: number, perPage: number = 20, page: number = 1, from: string = '', till: string = ''): Observable<Paginate<DocHistory>> {
    return this.http.get<Paginate<DocHistory>>(
      `${environment.base_url}/documents/${projectId}/get_all_history?page=${page}&per_page=${perPage}&from=${from}&till=${till}`
    );
  }

  restoreHistory(docHistoryId: number): Observable<any> {
    return this.http.get(`${environment.base_url}/documents/${docHistoryId}/restore_history`);
  }

  getStudies(projectId: number, searchTerm: string): Observable<any> {
    return this.http.get<PagedData<any>>(`${environment.base_url}/documents/${projectId}/get_references?q=${searchTerm}`);
  }
}
